/**
 * Utilities for legal document rendering
 */

export interface TocEntry {
  id: string;
  title: string;
  level: number;
}

/**
 * Slugify a string for use as an anchor ID
 */
export function slugify(text: string): string {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')           // Replace spaces with -
    .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
    .replace(/\-\-+/g, '-')         // Replace multiple - with single -
    .replace(/^-+/, '')             // Trim - from start of text
    .replace(/-+$/, '');            // Trim - from end of text
}

/**
 * Extract table of contents from markdown content
 * Parses ## and ### headings to build a structured TOC
 */
export function extractTableOfContents(markdown: string): TocEntry[] {
  const lines = markdown.split('\n');
  const entries: TocEntry[] = [];
  
  for (const line of lines) {
    // Match ## and ### headings (skip # which is typically the title)
    const match = line.match(/^(#{2,3})\s+(.+)$/);
    if (match) {
      const level = match[1].length;
      const title = match[2].trim();
      const id = slugify(title);
      
      entries.push({ id, title, level });
    }
  }
  
  return entries;
}

/**
 * Get only top-level sections (## headings) for main TOC
 */
export function extractMainSections(markdown: string): TocEntry[] {
  return extractTableOfContents(markdown).filter(entry => entry.level === 2);
}
